import { queryOptions, useQuery, keepPreviousData } from '@tanstack/react-query'
import { apiClient, authApiClient } from '~/lib/api-client'

export interface UIDiscoveryParams {
  lat: number
  lng: number
  radius: number
  categoryId?: number
  query?: string
  budget?: number
  nonSmoking?: boolean
  hasPrivateRoom?: boolean
  kidsAllowed?: boolean
  snapshotId?: string
  favoritesOnly?: boolean
  onlyShowSelected?: boolean
}

export interface NearbyShopsResponse {
  data: any[];
  pagination: { total: number; currentPage: number; pageSize: number; totalPages: number } | null;
}

export async function fetchNearbyShops(locale: string, params: UIDiscoveryParams, pageParam: number = 1): Promise<NearbyShopsResponse> {
  // @ts-ignore: dynamic query construction
  const apiQuery: any = {
    latitude: String(params.lat),
    longitude: String(params.lng),
    radius: String(params.radius),
    page: String(pageParam),
    pageSize: '20', // Default to 20 for infinite scroll
  }

  if (params.categoryId) {
    apiQuery.categoryIds = [String(params.categoryId)]
  }

  if (params.query) {
    apiQuery.query = params.query
  }
  if (params.budget) {
    apiQuery.budget = String(params.budget)
  }
  if (params.nonSmoking) {
    apiQuery.nonSmoking = String(params.nonSmoking)
  }
  if (params.hasPrivateRoom) {
    apiQuery.hasPrivateRoom = String(params.hasPrivateRoom)
  }
  if (params.kidsAllowed) {
    apiQuery.kidsAllowed = String(params.kidsAllowed)
  }
  if (params.snapshotId) {
    apiQuery.snapshotId = params.snapshotId
  }
  if (params.favoritesOnly) {
    apiQuery.favoritesOnly = String(params.favoritesOnly)
  }

  const client = params.favoritesOnly ? authApiClient : apiClient
  
  const res = await client.shops.nearby[':locale'].$get({
    param: { locale },
    query: apiQuery
  })
  
  if (!res.ok) {
    console.error("Failed to fetch nearby shops", await res.text())
    return { data: [], pagination: null }
  }
  
  const json = await res.json()
  return json as unknown as NearbyShopsResponse
}

export function nearbyShopsQueryOptions(locale: string, params: UIDiscoveryParams, options?: any) {
  return queryOptions({
    queryKey: ['nearby-shops', locale, params],
    queryFn: () => fetchNearbyShops(locale, params, 1),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function useNearbyShops(locale: string, params: UIDiscoveryParams, options?: any) {
  return useQuery(nearbyShopsQueryOptions(locale, params, options))
}

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'

export function nearbyShopsInfiniteQueryOptions(locale: string, params: UIDiscoveryParams, options?: any) {
  return infiniteQueryOptions({
    queryKey: ['nearby-shops', 'infinite', locale, params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => fetchNearbyShops(locale, params, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage: NearbyShopsResponse, allPages, lastPageParam) => {
      if (!lastPage || !lastPage.pagination) return undefined
      const { currentPage, totalPages } = lastPage.pagination
      if (currentPage >= totalPages) return undefined
      return currentPage + 1
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    ...options,
  })
}

export function useInfiniteNearbyShops(locale: string, params: UIDiscoveryParams, options?: any) {
  return useInfiniteQuery(nearbyShopsInfiniteQueryOptions(locale, params, options))
}
